import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";

/**
 * Custom withRouter HOC for React Router v6 compatibility
 *
 * This provides an API similar to the React Router v5's withRouter HOC
 * to minimize code changes during migration to React Router v6
 */
export const withRouter = (WrappedComponent) => {
  const WithRouterWrapper = (props) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    // Determine the path pattern based on the URL pattern
    let path = "";

    // Extract path segments for analysis
    const pathSegments = location.pathname.split("/").filter(Boolean);

    // Handle review URLs specifically
    if (location.pathname.includes("/review")) {
      if (pathSegments[0] === "c") {
        path = "/c/:companySlug/:projectName/review";
      } else if (pathSegments[0] === "g") {
        path = "/g/:companyID/:projectName/review";
      }
    }
    // Handle project URLs
    else if (pathSegments.length === 3 && pathSegments[0] === "c") {
      path = "/c/:companySlug/:projectName";
    } else if (pathSegments.length === 3 && pathSegments[0] === "g") {
      path = "/g/:companyID/:projectName";
    }
    // Handle company URLs
    else if (pathSegments.length === 2 && pathSegments[0] === "c") {
      path = "/c/:companySlug";
    } else if (pathSegments.length === 2 && pathSegments[0] === "g") {
      path = "/g/:companyID";
    } else {
      // Default to the pathname for other routes
      path = location.pathname;
    }

    // Create a history-like object that mimics React Router v5's history
    const history = {
      push: (path, state) => navigate(path, { state }),
      replace: (path, state) => navigate(path, { replace: true, state }),
      go: (n) => navigate(n),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      listen: () => () => {}, // Stub function that returns cleanup function
      block: () => () => {}, // Stub function that returns cleanup function
      createHref: (location) => {
        const path = typeof location === "string" ? location : location.pathname;
        return path;
      },
      location, // Original location object
    };

    // Create a match-like object to mimic React Router v5's match
    const match = {
      params,
      isExact: location.pathname === location.pathname.split("?")[0],
      path: path,
      url: location.pathname,
    };

    // For debugging - uncomment if needed
    // console.log("withRouter path:", path);
    // console.log("withRouter params:", params);
    // console.log("withRouter location:", location);

    return <WrappedComponent {...props} history={history} location={location} match={match} navigate={navigate} />;
  };

  // Display name for debugging
  const displayName = WrappedComponent.displayName || WrappedComponent.name || "Component";
  WithRouterWrapper.displayName = `withRouter(${displayName})`;

  return WithRouterWrapper;
};

export default withRouter;
